import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Users, Receipt, Award, Calendar as CalendarIcon, DollarSign, Copy, TrendingUp } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bento-card p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--app-text-muted)" }}>{label}</div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: accent || "var(--app-surface-2)" }}>
          <Icon className="w-4 h-4" style={{ color: "var(--app-primary)" }} />
        </div>
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}

export default function TeacherDashboard() {
  const { user, subscription } = useAuth();
  const t = useT();
  const [metrics, setMetrics] = useState(null);
  const [invite, setInvite] = useState("");
  const [todaysEvents, setTodaysEvents] = useState([]);
  const [students, setStudents] = useState([]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const [m, link, st, evs] = await Promise.all([
          apiClient.get("/metrics/teacher"),
          apiClient.get("/auth/invite-link"),
          apiClient.get("/students"),
          apiClient.get(`/lessons?start=${today}&end=${today}`),
        ]);
        setMetrics(m.data); setInvite(link.data.invite_code);
        setStudents(st.data.students); setTodaysEvents(evs.data.events);
      } catch (e) { toast.error(t("Failed to load dashboard")); }
    })();
  }, [today, location.key]);

  const inviteLink = `${window.location.origin}/join/${invite}`;

  return (
    <div className="space-y-8" data-testid="teacher-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--app-text-muted)" }}>
            {t("Welcome back, maestro")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold">{t("Hello,")} {user?.name?.split(" ")[0]} 🎹</h1>
          <p className="mt-1" style={{ color: "var(--app-text-muted)" }}>
            {subscription?.trial ? `${subscription.trial_days_left} ${t("trial days left.")}` : subscription?.active ? t("Your studio is active.") : t("Your trial has ended.")}
          </p>
        </div>
        <Link to="/teacher/students/new" className="btn-pill btn-primary" data-testid="quick-add-student">{t("+ Add Student")}</Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label={t("Students")} value={metrics?.students_count ?? "—"} icon={Users} />
        <Stat label={t("Weekly revenue")} value={`$${metrics?.weekly_revenue ?? 0}`} icon={TrendingUp} />
        <Stat label={t("Monthly revenue")} value={`$${metrics?.monthly_revenue ?? 0}`} icon={DollarSign} />
        <Stat label={t("Recitals")} value={metrics?.recitals_count ?? 0} icon={Award} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bento-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-bold flex items-center gap-2"><CalendarIcon className="w-5 h-5" /> {t("Today's lessons")}</h2>
            <Link to="/teacher/calendar" className="text-sm font-semibold" style={{ color: "var(--app-primary)" }}>{t("Open calendar →")}</Link>
          </div>
          {todaysEvents.length === 0 ? (
            <p style={{ color: "var(--app-text-muted)" }} data-testid="no-lessons-today">{t("No lessons today. Enjoy the quiet!")}</p>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--app-border)" }}>
              {todaysEvents.map((e) => (
                <li key={e.id} className="py-3 flex items-center justify-between" data-testid={`today-event-${e.id}`}>
                  <div>
                    <div className="font-semibold">{e.type === "recital" ? `🎤 ${e.title}` : `🎹 ${e.student_name}`}</div>
                    <div className="text-sm" style={{ color: "var(--app-text-muted)" }}>{e.time} · {e.duration_minutes} {t("min")}</div>
                  </div>
                  {e.type === "lesson" && (
                    <Link to={`/teacher/students/${e.student_id}`} className="text-sm font-semibold" style={{ color: "var(--app-primary)" }}>{t("Open →")}</Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bento-card p-6">
          <h2 className="font-display text-xl font-bold">{t("Invite students")}</h2>
          <p className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>{t("Share this link with students or parents to join your studio.")}</p>
          <div className="mt-3 flex gap-2">
            <input readOnly value={inviteLink} className="flex-1 rounded-xl border px-3 py-2 text-xs font-mono"
                   style={{ borderColor: "var(--app-border)" }} data-testid="invite-link-input" />
            <Button onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success(t("Copied!")); }}
                    variant="outline" size="icon" data-testid="copy-invite">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <div className="mt-5 text-xs" style={{ color: "var(--app-text-muted)" }}>
            {t("Invite code:")} <span className="font-mono font-bold">{invite}</span>
          </div>
        </div>
      </div>

      <div className="bento-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Users className="w-5 h-5" /> {t("My students")}</h2>
          <Link to="/teacher/students" className="text-sm font-semibold" style={{ color: "var(--app-primary)" }}>{t("View all →")}</Link>
        </div>
        {students.length === 0 ? (
          <div className="text-center py-8" data-testid="no-students">
            <div className="text-5xl mb-2">🎵</div>
            <p style={{ color: "var(--app-text-muted)" }}>{t("Add your first student to get started.")}</p>
            <Link to="/teacher/students/new" className="btn-pill btn-primary mt-4 inline-flex">{t("+ Add Student")}</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {students.slice(0, 6).map((s) => (
              <Link key={s.id} to={`/teacher/students/${s.id}`} className="rounded-2xl p-4 border-2 hover:-translate-y-0.5 transition-all"
                style={{ borderColor: "var(--app-border)", background: "var(--app-surface-2)" }}
                data-testid={`student-card-${s.id}`}>
                <div className="font-semibold">{s.name}</div>
                <div className="text-xs" style={{ color: "var(--app-text-muted)" }}>
                  {s.session_minutes}{t("-min sessions")} · {(s.frequency_days || []).length} {t("days/wk")}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
